/**
 * Paylaşılan checkout kilidi — minimal guard (kese kurgusuz temalar için).
 *
 * hp-auto-bag-control.js'in YALNIZCA checkout-guard kısmının çıkarımıdır
 * (bkz. campaign-gift-product-kurulum.md §6.1). Bu temada kese (bag) kurgusu
 * YOKTUR; hp-auto-campaign-control.js ve main-cart.liquid'in parse-time
 * kilidi `window.HPAutoBagControl.syncCheckoutGuards / isAnyCartSyncBusy`
 * isimlerini aradığından, sınıf adı API uyumluluğu için korunmuştur. İleride
 * gerçek kese kurgusu (hp-auto-bag-control.js) kurulursa bu dosya
 * KALDIRILMALIDIR — ikisi aynı global'i tanımlar.
 *
 * Görevleri:
 * 1. Kampanya hediyesi mutasyonu (HPAutoCampaignControl.isBusy) sürerken sepet
 *    sayfasındaki ödeme butonlarını disable eder ve cart formunun submit'ini
 *    capture fazında engeller.
 * 2. main-cart.liquid'in parse-time kilidini, kampanya kontrolü ilk kontrolünü
 *    tamamlayınca (initialSettled) serbest bırakır.
 * 3. Sepet re-render'ları (#main-cart innerHTML değişimi) buton state'ini
 *    sıfırladığından, kilidi MutationObserver ile yeniden uygular.
 *
 * @class HPAutoBagControl
 */
class HPAutoBagControl {
  /** Yalnızca guard'ın disable ettiği checkout butonlarını işaretleyen class. */
  static GUARD_CLASS = 'cart-sync-disabled';

  /** Ek ödeme butonu (Shop Pay vb.) sarmalayıcılarını kilitleyen class. */
  static GUARD_WRAPPER_CLASS = 'cart-syncing';

  /**
   * @type {{initialSettled: boolean}}
   * Kese kurgusu olmadığından shim'in kendi kontrolü yoktur; settled doğar.
   */
  static state = {
    initialSettled: true,
  };

  /**
   * Kese operasyonu var mı? Bu temada kese kurgusu yok — daima false.
   *
   * @returns {boolean}
   */
  static isBusy() {
    return false;
  }

  /**
   * Kese VEYA kampanya hediyesi mutasyonu sürüyor mu? Checkout kilidinin
   * disable koşulu.
   *
   * @returns {boolean}
   */
  static isAnyCartSyncBusy() {
    if (HPAutoBagControl.isBusy()) return true;
    const campaign = window.HPAutoCampaignControl;
    if (campaign && typeof campaign.isBusy === 'function' && campaign.isBusy()) return true;
    return false;
  }

  /**
   * Tüm kontroller sayfa yüklenişindeki ilk kontrolünü tamamladı mı?
   * Parse-time kilit (main-cart inline script) ancak o zaman serbest bırakılır.
   * Yüklenmemiş/başarısız script settled sayılır (fail-open).
   *
   * @returns {boolean}
   */
  static allControlsSettled() {
    if (!HPAutoBagControl.state.initialSettled) return false;
    const campaign = window.HPAutoCampaignControl;
    if (campaign && campaign.initialSettled === false) return false;
    return true;
  }

  /**
   * Bu kontrolün ilk kontrolünü tamamlandı olarak işaretler (API uyumluluğu).
   */
  static markSettled() {
    HPAutoBagControl.state.initialSettled = true;
  }

  /**
   * Checkout butonlarının kilit durumunu mevcut busy/settled durumuna göre
   * uygular. Yalnızca GUARD_CLASS ile guard'ın işaretlediği butonlara dokunur;
   * başka sebeple disabled edilmiş butonlar serbest bırakılmaz.
   */
  static syncCheckoutGuards() {
    const busy = HPAutoBagControl.isAnyCartSyncBusy();
    const limitsViolated = window.HPCartLimits?.isViolated?.() ?? false;
    const shouldLock = busy || limitsViolated;
    const releasable = !shouldLock && HPAutoBagControl.allControlsSettled();

    document.querySelectorAll('button[name="checkout"]').forEach((btn) => {
      if (shouldLock) {
        btn.disabled = true;
        if (busy) {
          btn.setAttribute('aria-busy', 'true');
        } else {
          btn.removeAttribute('aria-busy');
        }
        btn.classList.add(HPAutoBagControl.GUARD_CLASS);
      } else if (releasable && btn.classList.contains(HPAutoBagControl.GUARD_CLASS)) {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.classList.remove(HPAutoBagControl.GUARD_CLASS);
      }
    });

    if (shouldLock || releasable) {
      document
        .querySelectorAll('.additional-checkout-buttons--vertical')
        .forEach((el) => el.classList.toggle(HPAutoBagControl.GUARD_WRAPPER_CLASS, shouldLock));
    }
  }

  /**
   * Kampanya operasyonu sürerken cart formunun submit edilmesini (Enter dahil,
   * hangi buton üzerinden gelirse gelsin) capture fazında engeller. Disabled
   * attribute'u re-render ile silinse bile bu kilit çalışır.
   *
   * @param {SubmitEvent} event
   */
  static handleGuardedSubmit(event) {
    const limitsViolated = window.HPCartLimits?.isViolated?.() ?? false;
    if (!HPAutoBagControl.isAnyCartSyncBusy() && !limitsViolated) return;
    const form = event.target;
    if (form && (form.id === 'cart' || form.hasAttribute?.('data-cart-form'))) {
      if (limitsViolated) {
        window.HPCartLimits?.showToast?.(window.HPCartLimits.messages.total);
      }
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /**
   * Sepet sayfasındaki re-render'lar (#main-cart innerHTML değişimi) buton
   * state'ini sıfırlar; observer her değişimden sonra kilidi yeniden uygular.
   */
  static observeCartRerenders() {
    const mainCart = document.getElementById('main-cart');
    if (!mainCart || typeof MutationObserver === 'undefined') return;

    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        HPAutoBagControl.syncCheckoutGuards();
      });
    });
    observer.observe(mainCart, { childList: true, subtree: true });
  }

  /**
   * Event listener'ları kaydeder ve kilidi ilk kez senkronize eder.
   */
  static init() {
    // Checkout kilidi: form submit'i capture fazında engelle.
    document.addEventListener('submit', HPAutoBagControl.handleGuardedSubmit, true);

    HPAutoBagControl.observeCartRerenders();

    HPAutoBagControl.syncCheckoutGuards();
  }
}

// Global erişim için window'a bağla (campaign JS ve parse-time kilit bu ismi arar)
window.HPAutoBagControl = HPAutoBagControl;

// Sayfa yüklendiğinde event listener'ı aktif et
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => HPAutoBagControl.init());
} else {
  HPAutoBagControl.init();
}
